import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Car, AlertCircle } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();

  // Clear session expired message after showing it
  useEffect(() => {
    if (sessionExpired) {
      const timer = setTimeout(() => {
        clearSessionExpired();
      }, 10000); // Clear after 10 seconds
      return () => clearTimeout(timer);
    }
  }, [sessionExpired, clearSessionExpired]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password, rememberMe);
    if (result.success) {
      navigate('/shops');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Background image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.pexels.com/photos/5864164/pexels-photo-5864164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="absolute inset-0 bg-[#0F172A]/80" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl bg-[#2563EB] flex items-center justify-center">
              <Car className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              AutoPilot
            </h1>
          </div>
          <p className="text-[#94A3B8] text-lg text-center max-w-md">
            Super Admin Dashboard for managing auto shops, subscriptions, and agents.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#0F172A]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#2563EB] flex items-center justify-center">
              <Car className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              AutoPilot
            </h1>
          </div>

          <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-[#E2E8F0] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Welcome back
              </h2>
              <p className="text-[#94A3B8] text-sm">
                Sign in to access your admin dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {sessionExpired && (
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-3 flex items-start gap-2" data-testid="session-expired-alert">
                  <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#F59E0B] text-sm font-medium">Session Expired</p>
                    <p className="text-[#F59E0B]/80 text-xs mt-0.5">Your session has expired. Please login again to continue.</p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#E2E8F0] text-sm font-medium">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@autopilot.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11"
                  data-testid="email-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#E2E8F0] text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 pr-10"
                    data-testid="password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#94A3B8] transition-colors"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-[#334155] data-[state=checked]:bg-[#2563EB] data-[state=checked]:border-[#2563EB]"
                  data-testid="remember-checkbox"
                />
                <Label htmlFor="remember" className="text-[#94A3B8] text-sm cursor-pointer">
                  Remember me
                </Label>
              </div>

              {error && (
                <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3" data-testid="login-error">
                  <p className="text-[#EF4444] text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 font-medium transition-colors"
                data-testid="login-button"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#334155]">
              <p className="text-[#64748B] text-xs text-center">
                Super Admin Login<br />
                <span className="text-[#94A3B8]">Email:</span> admin@autoshop.com<br />
                <span className="text-[#94A3B8]">Password:</span> admin@123
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
